import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { StatusBadge } from '@/components/ui/badge'
import { DataTable } from '@/components/data/data-table'
import { useAuth } from '@/hooks/use-auth'
import { useIsAdmin } from '@/hooks/use-permissions'
import { toast } from 'sonner'
import { useState } from 'react'

interface TeamMember {
  id: string
  email: string
  name: string
  role: string
  status: string
  last_login_at: string | null
  created_at: string
}

interface InviteResponse {
  id: string
  email: string
  name?: string
  role?: string
  invitationExpiresInDays: number
  emailDelivered: boolean
}

export function SettingsTeamPage() {
  const { user } = useAuth()
  const isAdmin = useIsAdmin()
  const queryClient = useQueryClient()
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteName, setInviteName] = useState('')
  const [inviteRole, setInviteRole] = useState('tenant_viewer')
  const [showInvite, setShowInvite] = useState(false)
  const [lastInvite, setLastInvite] = useState<InviteResponse | null>(null)

  const { data: team, isLoading } = useQuery({
    queryKey: ['team'],
    queryFn: () => api.get<TeamMember[]>('/team'),
  })

  const inviteMutation = useMutation({
    mutationFn: () =>
      api.post<InviteResponse>('/team', {
        email: inviteEmail,
        name: inviteName,
        role: inviteRole,
      }),
    onSuccess: (data) => {
      if (data.emailDelivered) {
        toast.success(`Invitación enviada por email a ${data.email}.`)
      } else {
        toast.warning(
          `Invitación creada para ${data.email}, pero el envío de email falló. Pulsa "Reenviar invitación" más tarde o contacta al usuario por otro canal.`,
          { duration: 8000 },
        )
      }
      queryClient.invalidateQueries({ queryKey: ['team'] })
      setShowInvite(false)
      setInviteEmail('')
      setInviteName('')
      setLastInvite(data)
    },
    onError: (err) => toast.error((err as Error).message),
  })

  const resendMutation = useMutation({
    mutationFn: (id: string) => api.post<InviteResponse>(`/team/${id}/resend-invite`),
    onSuccess: (data) => {
      if (data.emailDelivered) {
        toast.success(`Nueva invitación enviada por email a ${data.email}.`)
      } else {
        toast.warning(
          `Invitación regenerada para ${data.email}, pero el envío de email falló. Inténtalo de nuevo en unos minutos.`,
          { duration: 8000 },
        )
      }
      setLastInvite(data)
    },
    onError: (err) => toast.error((err as Error).message),
  })

  const removeMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/team/${id}`),
    onSuccess: () => {
      toast.success('Usuario eliminado')
      queryClient.invalidateQueries({ queryKey: ['team'] })
    },
  })

  return (
    <Card>
      <CardHeader>
        <CardTitle>Equipo</CardTitle>
        {isAdmin && (
          <Button size="sm" onClick={() => setShowInvite(!showInvite)}>
            {showInvite ? 'Cancelar' : 'Invitar usuario'}
          </Button>
        )}
      </CardHeader>

      {showInvite && (
        <div className="mb-4 flex gap-3 rounded-lg border p-4" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-gray-50)' }}>
          <input
            type="text" placeholder="Nombre" value={inviteName}
            onChange={(e) => setInviteName(e.target.value)}
            className="flex-1 rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border)' }}
          />
          <input
            type="email" placeholder="Email" value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            className="flex-1 rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border)' }}
          />
          <select
            value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}
            className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border)' }}
          >
            <option value="tenant_viewer">Visor</option>
            <option value="tenant_manager">Gestor</option>
            <option value="tenant_admin">Admin</option>
          </select>
          <Button size="sm" onClick={() => inviteMutation.mutate()} loading={inviteMutation.isPending}>
            Enviar
          </Button>
        </div>
      )}

      {lastInvite && (
        <div
          className="mb-4 rounded-lg border p-4"
          style={
            lastInvite.emailDelivered
              ? { borderColor: 'var(--color-brand-secondary)', backgroundColor: 'var(--color-brand-primary-50)' }
              : { borderColor: 'var(--color-warning)', backgroundColor: 'var(--color-warning-light)' }
          }
        >
          <div className="mb-2 flex items-center justify-between">
            <p
              className="text-sm font-semibold"
              style={{ color: lastInvite.emailDelivered ? 'var(--color-brand-primary)' : 'var(--color-warning-dark)' }}
            >
              {lastInvite.emailDelivered
                ? `Invitación enviada a ${lastInvite.email}`
                : `Invitación pendiente de envío para ${lastInvite.email}`}
            </p>
            <button
              type="button"
              onClick={() => setLastInvite(null)}
              className="text-xs"
              style={{ color: 'var(--color-text-tertiary)' }}
            >
              Cerrar
            </button>
          </div>
          <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
            {lastInvite.emailDelivered
              ? `El enlace de activación se ha entregado por email. Caduca en ${lastInvite.invitationExpiresInDays} días y solo es válido una vez. Si no aparece, dile al usuario que revise la carpeta de spam.`
              : `La invitación se ha creado pero el envío de email falló (problema temporal de Resend o configuración pendiente). Pulsa "Reenviar invitación" pasados unos minutos.`}
          </p>
        </div>
      )}

      <DataTable
        columns={[
          { key: 'name', header: 'Nombre' },
          { key: 'email', header: 'Email' },
          {
            key: 'role', header: 'Rol',
            render: (r) => {
              const role = (r as Record<string, string>).role
              if (role === 'super_admin') return 'Super Admin'
              if (role === 'tenant_admin') return 'Admin'
              if (role === 'tenant_manager') return 'Gestor'
              return 'Visor'
            },
          },
          {
            key: 'status', header: 'Estado',
            render: (r) => <StatusBadge status={(r as Record<string, string>).status} />,
          },
          {
            key: 'actions', header: '',
            render: (r) => {
              const row = r as Record<string, string>
              if (row.id === user?.id) return null
              if (!isAdmin) return null
              return (
                <div className="flex gap-1">
                  {row.status === 'invited' && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => resendMutation.mutate(row.id)}
                      loading={resendMutation.isPending && resendMutation.variables === row.id}
                    >
                      Reenviar invitación
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => removeMutation.mutate(row.id)}>
                    Eliminar
                  </Button>
                </div>
              )
            },
          },
        ]}
        data={(team ?? []) as unknown as Record<string, unknown>[]}
        loading={isLoading}
      />
    </Card>
  )
}
